import {
  Box,
  Button,
  Card,
  CardHeader,
  Chip,
  FormControl,
  InputAdornment,
  InputLabel,
  Menu,
  MenuItem,
  Pagination,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Grid,
  Select,
  TextField,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
} from "@mui/material";

import CloseIcon from "@mui/icons-material/Close";
import React, { useEffect, useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  confirmOrder,
  deleteOrder,
  deliveredOrder,
  getOrders,
  shipOrder,
} from "../../../Redux/Admin/Orders/Action";

import SearchIcon from "@mui/icons-material/Search";
import { API_BASE_URL } from "../../../config/api";
import axios from "axios";

const OrdersTable = () => {
  const [formData, setFormData] = useState({ status: "", sort: "", search: "" });
  const [anchorElArray, setAnchorElArray] = useState([]);
  const [searchResults, setSearchResults] = useState([]);
  const [searchActive, setSearchActive] = useState(false);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const [trackingModalOpen, setTrackingModalOpen] = useState(false);
  const [trackingData, setTrackingData] = useState([]);
  const [trackingLoading, setTrackingLoading] = useState(false);
  const [trackingError, setTrackingError] = useState("");
  const [selectedAWB, setSelectedAWB] = useState("");

  const dispatch = useDispatch();
  const jwt = localStorage.getItem("jwt");
  const { adminsOrder } = useSelector((store) => store);
  const debounceTimeout = useRef(null);

  // Fetch orders based on pagination, filter, sort
  useEffect(() => {
    if (!searchActive) {
      setLoading(true);
      dispatch(
        getOrders({
          jwt,
          status: formData.status,
          sort: formData.sort,
          page: currentPage,
          limit: itemsPerPage,
        })
      ).finally(() => setLoading(false));
    }
  }, [jwt, formData.status, formData.sort, currentPage, adminsOrder.delivered, adminsOrder.shipped, adminsOrder.confirmed, searchActive]);

  const ordersToDisplay = searchActive ? searchResults.orders : adminsOrder.orders?.orders || [];

  const handleUpdateStatusMenuClick = (event, index) => {
    const newAnchorElArray = [...anchorElArray];
    newAnchorElArray[index] = event.currentTarget;
    setAnchorElArray(newAnchorElArray);
  };

  const handleUpdateStatusMenuClose = (index) => {
    const newAnchorElArray = [...anchorElArray];
    newAnchorElArray[index] = null;
    setAnchorElArray(newAnchorElArray);
  };

  const performSearch = (searchQuery) => {
    if (searchQuery.trim() !== "") {
      setSearchActive(true);
      setLoading(true);
      let url = `${API_BASE_URL}/api/orders/search?query=${searchQuery}`;
      fetch(url, {
        headers: { Authorization: `Bearer ${jwt}` },
      })
        .then((res) => res.json())
        .then((data) => {
          setSearchResults(data);
          setLoading(false);
        })
        .catch((err) => {
          console.error("Search error:", err);
          setLoading(false);
        });
    } else {
      setSearchActive(false);
      setSearchResults([]);
      setLoading(true);
      dispatch(
        getOrders({
          jwt,
          status: formData.status,
          sort: formData.sort,
          page: currentPage,
          limit: itemsPerPage,
        })
      ).finally(() => setLoading(false));
    }
  };

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData((prev) => ({ ...prev, [name]: value }));

    if (name === "search") {
      if (debounceTimeout.current) clearTimeout(debounceTimeout.current);
      debounceTimeout.current = setTimeout(() => {
        performSearch(value);
      }, 500);
    } else {
      setCurrentPage(1); // Reset to first page when status or sort changes
    }
  };

  const handlePaginationChange = (event, value) => {
    setCurrentPage(value);
  };

  const handleConfirmedOrder = (orderId, index) => {
    handleUpdateStatusMenuClose(index);
    dispatch(confirmOrder(orderId));
  };

  const handleShippedOrder = (orderId, index) => {
    handleUpdateStatusMenuClose(index);
    dispatch(shipOrder(orderId));
  };

  const handleDeliveredOrder = (orderId, index) => {
    handleUpdateStatusMenuClose(index);
    dispatch(deliveredOrder(orderId));
  };

  const handleDeleteOrder = (orderId) => {
    dispatch(deleteOrder(orderId));
  };

  const handleDownloadPdf = (byteArray) => {
    try {
      const uint8Array = new Uint8Array(byteArray);
      const blob = new Blob([uint8Array], { type: "application/pdf" });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = "BlueDart_AWB.pdf";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      console.error("Failed to download AWB PDF", err);
    }
  };

  // ✅ Track Order API
  const fetchTracking = async (awbNo) => {
    setTrackingLoading(true);
    setTrackingError("");
    setTrackingData([]);
    try {
      const response = await axios.get(`${API_BASE_URL}/api/orders/track-order`, {
        params: { awbNo },
      });

      const shipment = response.data.shipment[0];
      const scans = shipment?.Scans
        ? shipment.Scans.map((s) => s.ScanDetail).reverse()
        : [];

      setTrackingData(
        scans.length
          ? scans
          : [
            {
              Scan: shipment.Status,
              ScannedLocation: `${shipment.Origin} → ${shipment.Destination}`,
              ScanDate: shipment.StatusDate,
              ScanTime: shipment.StatusTime,
            },
          ]
      );
    } catch (error) {
      setTrackingError("Failed to fetch tracking data.");
      console.error("Tracking fetch error:", error.response?.data || error.message);
    } finally {
      setTrackingLoading(false);
    }
  };

  const handleOpenTrackingModal = (awbNo) => {
    setSelectedAWB(awbNo);
    setTrackingModalOpen(true);
    fetchTracking(awbNo);
  };

  const handleCloseTrackingModal = () => {
    setTrackingModalOpen(false);
    setTrackingData([]);
    setTrackingError("");
    setSelectedAWB("");
  };

  return (
    <Box sx={{ pt: "60px" }}>
      {/* Search Bar */}
      <Box
        sx={{
          mb: 2,
          display: "flex",
          gap: 1,
          position: "fixed",
          top: 50,
          right: 0,
          backgroundColor: "white",
          zIndex: 1100,
          p: 2,
          width: "85%",
        }}
      >
        <TextField
          fullWidth
          variant="outlined"
          placeholder="Search orders by name, email, product, address..."
          name="search"
          value={formData.search}
          onChange={handleChange}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
      </Box>

      {/* Sort & Filter */}
      {/* <Card className="p-3">
        <CardHeader
          title="Sort & Filter"
          sx={{ pt: 0, alignItems: "center", "& .MuiCardHeader-action": { mt: 0.6 } }}
        />
        <Grid container spacing={2}>
          <Grid item xs={4}>
            <FormControl fullWidth>
              <InputLabel id="status-select-label">Status</InputLabel>
              <Select
                labelId="status-select-label"
                id="status-select"
                name="status"
                value={formData.status}
                label="Status"
                onChange={handleChange}
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="PLACED">PLACED</MenuItem>
                <MenuItem value="PENDING">PENDING</MenuItem>
                <MenuItem value="CONFIRMED">CONFIRMED</MenuItem>
                <MenuItem value="DELIVERED">DELIVERED</MenuItem>
                <MenuItem value="CANCELLED">CANCELLED</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={4}>
            <FormControl fullWidth>
              <InputLabel id="sort-select-label">Sort By</InputLabel>
              <Select
                labelId="sort-select-label"
                id="sort-select"
                name="sort"
                value={formData.sort}
                label="Sort By"
                onChange={handleChange}
              >
                <MenuItem value="">None</MenuItem>
                <MenuItem value="Newest">Newest</MenuItem>
                <MenuItem value="Older">Older</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Card> */}
      {/* Sort & Filter */}
      <Box
        sx={{
          mb: 2,
          position: "fixed",
          top: 110, // search bar is at 50px, so place this below it
          right: 0,
          zIndex: 1099,
          width: "85%",
          backgroundColor: "white",
          p: 2,
          borderRadius: 1,
          boxShadow: 1,
        }}
      >
        <Card className="p-3">
          <CardHeader
            title="Sort & Filter"
            sx={{ pt: 0, alignItems: "center", "& .MuiCardHeader-action": { mt: 0.6 } }}
          />
          <Grid container spacing={2}>
            <Grid item xs={4}>
              <FormControl fullWidth>
                <InputLabel id="status-select-label">Status</InputLabel>
                <Select
                  labelId="status-select-label"
                  id="status-select"
                  name="status"
                  value={formData.status}
                  label="Status"
                  onChange={handleChange}
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="PLACED">PLACED</MenuItem>
                  <MenuItem value="PENDING">PENDING</MenuItem>
                  <MenuItem value="CONFIRMED">CONFIRMED</MenuItem>
                  <MenuItem value="DELIVERED">DELIVERED</MenuItem>
                  <MenuItem value="CANCELLED">CANCELLED</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={4}>
              <FormControl fullWidth>
                <InputLabel id="sort-select-label">Sort By</InputLabel>
                <Select
                  labelId="sort-select-label"
                  id="sort-select"
                  name="sort"
                  value={formData.sort}
                  label="Sort By"
                  onChange={handleChange}
                >
                  <MenuItem value="">None</MenuItem>
                  <MenuItem value="Newest">Newest</MenuItem>
                  <MenuItem value="Older">Older</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </Card>
      </Box>


      {/* Orders Table */}
      <Card className="mt-2">
        <CardHeader
          title="All Orders"
          sx={{ pt: 2, alignItems: "center", "& .MuiCardHeader-action": { mt: 0.6 } }}
        />
        <TableContainer sx={{ mt: "80px" }}>
          <Table sx={{ minWidth: 800 }} aria-label="table in dashboard">
            <TableHead>
              <TableRow>
                <TableCell>Products</TableCell>
                <TableCell>Customer Name</TableCell>
                <TableCell>Date</TableCell>
                <TableCell>Price</TableCell>
                <TableCell>Address</TableCell>
                <TableCell>OrderNumber</TableCell>
                <TableCell>AWB NO</TableCell>
                <TableCell>Track</TableCell>
                <TableCell>Download Label</TableCell>
                <TableCell>Phone Number</TableCell>
                <TableCell>Email</TableCell>
                <TableCell sx={{ textAlign: "center" }}>Status</TableCell>
                <TableCell sx={{ textAlign: "center" }}>Update</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={14} sx={{ textAlign: "center", py: 5 }}>
                    <CircularProgress />
                    <Typography sx={{ mt: 2 }}>Loading orders...</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                ordersToDisplay?.map((item, index) => (
                  <TableRow
                    hover
                    key={item._id}
                    sx={{ "&:last-of-type td, &:last-of-type th": { border: 0 } }}
                  >
                    <TableCell>
                      <Box sx={{ display: "flex", flexDirection: "column" }}>
                        <Typography sx={{ fontWeight: 500, fontSize: "0.875rem !important" }}>
                          {item?.orderItems.map((order) => (
                            <span key={order._id}>
                              {order.product?.title} = {order.quantity},{" "}
                            </span>
                          ))}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      {`${item?.shippingAddress?.firstName || item?.user?.firstName || ''} ${item?.shippingAddress?.lastName || item?.user?.lastName || ''}`}
                    </TableCell>
                    <TableCell>
                      {new Intl.DateTimeFormat("en-GB", {
                        day: "2-digit",
                        month: "short",
                        year: "numeric",
                        hour: "2-digit",
                        minute: "2-digit",
                        hour12: true,
                      }).format(new Date(item?.createdAt))}
                    </TableCell>
                    <TableCell>{item?.totalDiscountedPrice}</TableCell>
                    <TableCell>
                      {item?.shippingAddress?.streetAddress} {item?.shippingAddress?.state} {item?.shippingAddress?.zipCode}
                    </TableCell>
                    <TableCell>{item?.orderNumber}</TableCell>
                    <TableCell>{item?.awbNo || "N/A"}</TableCell>
                    <TableCell>
                      {item?.awbNo ? (
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={() => handleOpenTrackingModal(item.awbNo)}
                        >
                          Track
                        </Button>
                      ) : (
                        "N/A"
                      )}
                    </TableCell>
                    <TableCell>
                      <Button
                        onClick={() => handleDownloadPdf(item.awbPrint)}
                        disabled={!item.awbPrint || item.awbPrint.length === 0}
                        variant="outlined"
                        size="small"
                      >
                        Download
                      </Button>
                    </TableCell>
                    <TableCell>{item?.user?.mobile}</TableCell>
                    <TableCell>{item?.user?.email}</TableCell>
                    <TableCell sx={{ textAlign: "center" }}>
                      <Chip
                        sx={{ color: "white !important", fontWeight: "bold" }}
                        label={item?.orderStatus}
                        size="small"
                        color={
                          item.orderStatus === "PENDING"
                            ? "info"
                            : item.orderStatus === "DELIVERED"
                              ? "success"
                              : "secondary"
                        }
                      />
                    </TableCell>
                    <TableCell sx={{ textAlign: "center" }}>
                      <div>
                        <Button
                          id={`basic-button-${item?._id}`}
                          aria-controls={`basic-menu-${item._id}`}
                          aria-haspopup="true"
                          aria-expanded={Boolean(anchorElArray[index])}
                          onClick={(event) => handleUpdateStatusMenuClick(event, index)}
                        >
                          Status
                        </Button>
                        <Menu
                          id={`basic-menu-${item?._id}`}
                          anchorEl={anchorElArray[index]}
                          open={Boolean(anchorElArray[index])}
                          onClose={() => handleUpdateStatusMenuClose(index)}
                          MenuListProps={{ "aria-labelledby": `basic-button-${item._id}` }}
                        >
                          <MenuItem
                            onClick={() => handleConfirmedOrder(item?._id, index)}
                            disabled={
                              item.orderStatus === "DELIVERED" ||
                              item.orderStatus === "SHIPPED" ||
                              item.orderStatus === "CONFIRMED"
                            }
                          >
                            CONFIRMED ORDER
                          </MenuItem>
                          <MenuItem
                            onClick={() => handleShippedOrder(item._id, index)}
                            disabled={item.orderStatus === "DELIVERED" || item.orderStatus === "SHIPPED"}
                          >
                            SHIPPED ORDER
                          </MenuItem>
                          <MenuItem onClick={() => handleDeliveredOrder(item._id, index)}>
                            DELIVERED ORDER
                          </MenuItem>
                        </Menu>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>

      {/* Pagination */}
      {!searchActive && (
        <Card className="mt-2 flex justify-center items-center">
          <Pagination
            className="py-5 w-auto"
            size="large"
            count={Math.ceil((adminsOrder.orders?.total || 0) / itemsPerPage)}
            color="primary"
            page={currentPage}
            onChange={handlePaginationChange}
          />
        </Card>
      )}

      {/* Tracking Modal */}
      <Dialog open={trackingModalOpen} onClose={handleCloseTrackingModal} maxWidth="sm" fullWidth>
        <DialogTitle>
          Tracking for AWB: {selectedAWB}
          <IconButton
            aria-label="close"
            onClick={handleCloseTrackingModal}
            sx={{ position: "absolute", right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {trackingLoading ? (
            <Box sx={{ textAlign: "center", py: 5 }}>
              <CircularProgress />
              <Typography sx={{ mt: 2 }}>Loading tracking data...</Typography>
            </Box>
          ) : trackingError ? (
            <Typography color="error">{trackingError}</Typography>
          ) : trackingData?.length === 0 ? (
            <Typography>No tracking data available</Typography>
          ) : (
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Status</TableCell>
                  <TableCell>Location</TableCell>
                  <TableCell>Date</TableCell>
                  <TableCell>Time</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {trackingData.map((scan, idx) => (
                  <TableRow key={idx}>
                    <TableCell>{scan.Scan}</TableCell>
                    <TableCell>{scan.ScannedLocation}</TableCell>
                    <TableCell>{scan.ScanDate}</TableCell>
                    <TableCell>{scan.ScanTime}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseTrackingModal} color="primary">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default OrdersTable;
